import React from 'react';
import { TooltipContent, TooltipTrigger } from '../ui/tooltip';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { getCodeReferenceExternalUrl } from './CodeReferencesPanel';

const CODE_COLS = new Set([
  'code', 'icd-10', 'icd10', 'cpt', 'rxnorm', 'loinc',
  'code_system', 'medical_code', 'diagnosis_code', 'procedure_code',
]);

function isCodeCol(col: string): boolean {
  const lower = col.toLowerCase();
  if (CODE_COLS.has(lower) || lower.endsWith('_code')) return true;
  const parenMatch = lower.match(/code\s*\((.+?)\)/);
  if (parenMatch) return true;
  return false;
}

const KNOWN_CODE_SYSTEMS: Record<string, string> = {
  'icd-10': 'ICD-10',
  icd10: 'ICD-10',
  'icd-10-cm': 'ICD-10',
  rxnorm: 'RxNorm',
  loinc: 'LOINC',
  cpt: 'CPT',
  snomed: 'SNOMED CT',
  ndc: 'NDC',
};

function codeSystemFromHeader(col: string): string {
  const lower = col.toLowerCase().trim();
  const parenMatch = lower.match(/\(([^)]+)\)/);
  const token = (parenMatch ? parenMatch[1] : lower).trim();
  return KNOWN_CODE_SYSTEMS[token] ?? '';
}

const SYSTEM_ACCENT: Record<string, string> = {
  'ICD-10': 'bg-blue-500',
  RxNorm: 'bg-emerald-500',
  LOINC: 'bg-violet-500',
  CPT: 'bg-amber-500',
  NDC: 'bg-teal-500',
  'SNOMED CT': 'bg-rose-500',
};

function CodeChip({ code, system }: { code: string; system: string }) {
  const url = getCodeReferenceExternalUrl(system, code);
  const accent = SYSTEM_ACCENT[system] ?? 'bg-slate-500';
  return (
    <TooltipPrimitive.Root delayDuration={180}>
      <TooltipTrigger asChild>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-mono text-[12px] cursor-pointer hover:brightness-110 transition-all"
        >
          <span className={`w-1.5 h-1.5 rounded-full ${accent} shrink-0`} />
          {code}
        </a>
      </TooltipTrigger>
      <TooltipContent side="top" variant="clinical" className="max-w-xs text-left p-3">
        <div className="flex gap-2.5 min-w-[160px]">
          <div className={`${accent} w-1 rounded-full shrink-0`} />
          <div className="flex flex-col gap-1 py-0.5">
            <span className="text-[10px] uppercase tracking-widest font-medium text-slate-400">{system}</span>
            <span className="font-mono text-sm font-medium text-white tracking-wide">{code}</span>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-medium text-sky-400 hover:text-sky-300 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="underline underline-offset-2">Look up code</span>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
                <path d="M6.22 8.72a.75.75 0 0 0 1.06 1.06l5.22-5.22v1.69a.75.75 0 0 0 1.5 0v-3.5a.75.75 0 0 0-.75-.75h-3.5a.75.75 0 0 0 0 1.5h1.69L6.22 8.72Z" />
                <path d="M3.5 6.75c0-.69.56-1.25 1.25-1.25H7A.75.75 0 0 0 7 4H4.75A2.75 2.75 0 0 0 2 6.75v4.5A2.75 2.75 0 0 0 4.75 14h4.5A2.75 2.75 0 0 0 12 11.25V9a.75.75 0 0 0-1.5 0v2.25c0 .69-.56 1.25-1.25 1.25h-4.5c-.69 0-1.25-.56-1.25-1.25v-4.5Z" />
              </svg>
            </a>
          </div>
        </div>
      </TooltipContent>
    </TooltipPrimitive.Root>
  );
}

function cellValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return JSON.stringify(v);
}

/**
 * #104: collapse a column header or a row key to one comparable form.
 *
 * Table specs emit row keys in TWO conventions and nothing reconciled them.
 * Measured on the live round: the labs table keys its rows by the lowercased
 * header verbatim — `"code (loinc)"` — while the medications table keys the
 * same concept as `code_rxnorm`. The old lookup lowercased and turned spaces
 * into underscores, which produces `code_(rxnorm)` — parentheses and all — so
 * it matched the first convention and never the second. The column then had no
 * value on any row, and the empty-column pruning below silently dropped it.
 *
 * That silence is the actual defect: a column vanishing looks identical to a
 * column that was never specified, so the reader cannot tell a missing RxNorm
 * code from a table that simply does not carry one.
 *
 * Normalising both sides to alphanumeric-and-underscore bridges the two
 * conventions without either side having to know about the other.
 */
function normalizeCellKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function lookupCell(row: Record<string, unknown>, col: string): unknown {
  if (col in row) return row[col];
  const lower = col.toLowerCase();
  if (lower in row) return row[lower];
  const snake = col.toLowerCase().replace(/\s+/g, '_');
  if (snake in row) return row[snake];
  for (const k of Object.keys(row)) {
    if (k.toLowerCase() === lower) return row[k];
  }
  // #104: the normalised comparison, tried last so no existing exact match
  // can change meaning. "Code (RxNorm)" and "code_rxnorm" both reduce to
  // "code_rxnorm" here, which is what finally bridges the two conventions.
  const normalized = normalizeCellKey(col);
  if (normalized) {
    for (const k of Object.keys(row)) {
      if (normalizeCellKey(k) === normalized) return row[k];
    }
  }
  return undefined;
}

export function DataTable({
  columns,
  rows,
  severityBadges = false,
  compact = false,
}: {
  columns: string[];
  rows: Record<string, unknown>[];
  severityBadges?: boolean;
  compact?: boolean;
}) {
  if (!columns?.length || !rows?.length) return null;
  void severityBadges;

  // #4A: a column with no value on ANY row carries no information — it just
  // consumes width and forces the reader to assemble the answer from the
  // remaining columns. This emptiness test used to apply only to code columns;
  // it is a property of the data, not of the column's name, so it now applies
  // to every column in every resource type's spec. `hasAnyData` below still
  // guards the degenerate case where nothing at all is populated.
  const visibleColumns = columns.filter((col) =>
    rows.some((row) => {
      const v = lookupCell(row, col);
      return v != null && String(v).trim() !== '' && String(v).trim() !== '—';
    })
  );

  const hasAnyData = rows.some((row) =>
    visibleColumns.some((col) => {
      const v = lookupCell(row, col);
      return v != null && String(v).trim() !== '' && String(v).trim() !== '—';
    })
  );
  if (!hasAnyData) return null;

  const cellClass = compact ? 'px-2 py-1.5' : 'px-3 py-2';
  const thClass = compact ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-xs';

  // #4B: the old cap was a pixel height computed from an ASSUMED fixed row
  // height (41px). Any row whose cell text wraps to two lines is taller than
  // that, so a 7-row medication table showed 3 rows and clipped the 4th
  // mid-row while ~400px of page below it sat empty. Two changes: scroll only
  // past a row count well above a typical chart table, and cap by viewport
  // fraction so the height never depends on guessing how tall a row renders.
  const SCROLL_AFTER_ROWS = 25;
  const shouldScroll = rows.length > SCROLL_AFTER_ROWS;

  return (
    <div className="overflow-x-auto rounded-lg border border-border/70 bg-background">
      <div
        className={shouldScroll ? 'overflow-y-auto' : undefined}
        style={shouldScroll ? { maxHeight: '70vh' } : undefined}
      >
        <table className="w-full text-sm">
          <thead className={shouldScroll ? 'sticky top-0 z-10 bg-background' : undefined}>
            <tr className="border-b border-border/70">
              {visibleColumns.map((col) => (
                <th
                  key={col}
                  className={`${thClass} border-b border-border/70 font-medium text-text-tertiary text-left uppercase tracking-wider`}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={i}
                className="border-b border-border/60 last:border-b-0"
              >
                {visibleColumns.map((col, colIdx) => {
                  const val = lookupCell(row, col);
                  const isFirst = colIdx === 0;
                  let codeVal = cellValue(val);
                  const system = isCodeCol(col) ? codeSystemFromHeader(col) : '';
                  if (system && codeVal) {
                    codeVal = codeVal.replace(/\s+(RXNORM|ICD10|ICD-10|LOINC|CPT|SNOMED|NDC)\s*$/i, '').trim();
                  }
                  const showChip = system && codeVal && codeVal !== '—' && codeVal.trim().length > 0;
                  return (
                    <td
                      key={col}
                      className={`${cellClass} border-b border-border/50 text-foreground align-top ${isFirst ? 'font-medium' : ''}`}
                    >
                      {showChip ? (
                        <CodeChip code={codeVal} system={system} />
                      ) : (
                        <span className={`wrap-break-word ${isCodeCol(col) ? 'font-mono text-[12px]' : ''}`}>
                          {codeVal}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 5 && (
        <div className="px-3 py-1.5 text-[10px] text-text-tertiary/70 border-t border-border/60 text-right tabular-nums">
          {rows.length} records
        </div>
      )}
    </div>
  );
}
